import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BookableStorageLocation } from '@luggagehero/shared/interfaces';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedShopsService } from '@luggagehero/shared/services/shops';
import { TranslateService } from '@ngx-translate/core';

import { BaseComponent } from '../../../core';
import { ProductService } from '../../../services/storage-manager/product.service';
import { DateUtil } from '../../../utils/date.util';
import { StringUtil } from '../../../utils/string.util';

@Component({ template: '' })
export abstract class StorageLocationPricingBaseComponent extends BaseComponent implements OnInit {
  @ViewChild('form') form: NgForm;
  private static readonly PARENT_DEVIATION_MULTIPLIER = 5;
  public storageLocationId: string;
  public isModified = false;
  public isInitialized = false;

  private _hourlyRate: string;
  private _dailyRate: string;
  private _parentHourlyRateNumber: number;
  private _parentDailyRateNumber: number;

  private previousHourlyRate: string;
  private previousDailyRate: string;
  private _isCustomPricingEnabled = false;
  private wasCustomPricingEnabled = false;
  private _isLoading = false;

  constructor(
    private shopService: SharedShopsService,
    private productService: ProductService,
    private notify: SharedNotificationService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  public get shop(): BookableStorageLocation {
    return this.shopService.current && this.shopService.current._id === this.storageLocationId
      ? this.shopService.current
      : null;
  }
  public set shop(value: BookableStorageLocation) {
    this.shopService.setCurrent(value);
    this.cd.markForCheck();
  }

  public get hourlyRate(): string {
    return this._hourlyRate;
  }
  public set hourlyRate(value: string) {
    this._hourlyRate = value;
    this.updateIsModified();
  }

  public get dailyRate(): string {
    return this._dailyRate;
  }
  public set dailyRate(value: string) {
    this._dailyRate = value;
    this.updateIsModified();
  }

  public get parentHourlyRateNumber(): number {
    return this._parentHourlyRateNumber;
  }

  public set parentHourlyRateNumber(value: number) {
    this._parentHourlyRateNumber = value;
    this.updateIsModified();
  }

  public get parentDailyRateNumber(): number {
    return this._parentDailyRateNumber;
  }

  public set parentDailyRateNumber(value: number) {
    this._parentDailyRateNumber = value;
    this.updateIsModified();
  }

  public get hourlyRateNumber(): number {
    return parseFloat(this.hourlyRate);
  }

  public get dailyRateNumber(): number {
    return parseFloat(this.dailyRate);
  }

  public get currencySymbol(): string {
    if (!this.shop?.pricing) {
      return '';
    }
    return StringUtil.getCurrencySymbol(this.shop.pricing.currency);
  }

  public get isCustomPricingEnabled(): boolean {
    return this._isCustomPricingEnabled;
  }
  public set isCustomPricingEnabled(value: boolean) {
    this._isCustomPricingEnabled = value;
    void this.getCustomOrDefaultPricing();
  }

  public get isSaveButtonDisabled(): boolean {
    if (!this.form) {
      // Form UI is not initialized yet
      return true;
    }

    if (this.validationError) {
      // The current values of the input fields are invalid
      return true;
    }

    if (!this.isModified) {
      // Nothing has changed
      return true;
    }

    return false;
  }

  public get validationError(): string {
    if (this.dailyRateNumber < this.hourlyRateNumber * 2) {
      return this.translate.instant('DAILY_RATE_MUST_BE_AT_LEAST_TWICE_HOURLY_RATE') as string;
    }

    // make sure we respect the mulitplier both ways (/ and *)
    // the dailyRate cant be more than 5 times the parentDailyRate or less than 5 times the parentDailyRate
    // the hourlyRate cant be more than 5 times the parentHourlyRate or less than 5 times the parentHourlyRate
    if (
      this.dailyRateNumber >
      this.parentDailyRateNumber * StorageLocationPricingBaseComponent.PARENT_DEVIATION_MULTIPLIER
    ) {
      return this.translate.instant('DAILY_RATE_IS_TOO_HIGH') as string;
    }

    if (
      this.dailyRateNumber <
      this.parentDailyRateNumber / StorageLocationPricingBaseComponent.PARENT_DEVIATION_MULTIPLIER
    ) {
      return this.translate.instant('DAILY_RATE_IS_TOO_LOW') as string;
    }

    if (
      this.hourlyRateNumber >
      this.parentHourlyRateNumber * StorageLocationPricingBaseComponent.PARENT_DEVIATION_MULTIPLIER
    ) {
      return this.translate.instant('HOURLY_RATE_IS_TOO_HIGH') as string;
    }

    if (
      this.hourlyRateNumber <
      this.parentHourlyRateNumber / StorageLocationPricingBaseComponent.PARENT_DEVIATION_MULTIPLIER
    ) {
      return this.translate.instant('HOURLY_RATE_IS_TOO_LOW') as string;
    }

    if (this.form?.controls.hourlyRate?.errors?.required || this.hourlyRateNumber <= 0) {
      return this.translate.instant('HOURLY_RATE_MUST_BE_GREATER_THAN_ZERO') as string;
    }

    if (this.form?.controls.dailyRate?.errors?.required || this.dailyRateNumber <= 0) {
      return this.translate.instant('DAILY_RATE_MUST_BE_GREATER_THAN_ZERO') as string;
    }

    return '';
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public set isLoading(value: boolean) {
    this._isLoading = value;
    this.cd.markForCheck();
  }

  public ngOnInit() {
    this.route.params.subscribe((params: { id: string }) => {
      void (async () => {
        // Store storage location IDs from URL params
        this.storageLocationId = params['id'];

        await this.loadShop();

        const currentContextPricing = await this.productService.getWalkInPricesForStorageLocation(
          this.storageLocationId,
        );

        const parentContextPricing = await this.productService.getWalkInPricesForStorageLocation(
          this.storageLocationId,
          true,
        );

        this.wasCustomPricingEnabled = currentContextPricing.storageLocationSpecificContext;
        this.isCustomPricingEnabled = this.wasCustomPricingEnabled;

        this.previousHourlyRate = this.formatPrice(
          currentContextPricing.products.find((p) => p.key === 'WALK_IN_HOURLY_RATE').cost,
        );
        this.previousDailyRate = this.formatPrice(
          currentContextPricing.products.find((p) => p.key === 'WALK_IN_DAILY_RATE').cost,
        );

        this.parentHourlyRateNumber = parentContextPricing.products.find((p) => p.key === 'WALK_IN_HOURLY_RATE').cost;
        this.parentDailyRateNumber = parentContextPricing.products.find((p) => p.key === 'WALK_IN_DAILY_RATE').cost;

        this.hourlyRate = this.previousHourlyRate;
        this.dailyRate = this.previousDailyRate;

        if (
          this.hourlyRate === null ||
          this.hourlyRate === undefined ||
          this.dailyRate === null ||
          this.dailyRate === undefined ||
          this.parentHourlyRateNumber === null ||
          this.parentHourlyRateNumber === undefined ||
          this.parentDailyRateNumber === null ||
          this.parentDailyRateNumber === undefined
        ) {
          // TODO: Translate this
          this.notify.error(this.translate.instant('PRICES_COULD_NOT_BE_LOADED') as string);
        }

        this.isInitialized = true;
        this.cd.markForCheck();
      })();
    });
  }

  public onHourlyRateChange(value: string) {
    this.hourlyRate = this.formatPrice(value);
  }

  public onDailyRateChange(value: string) {
    this.dailyRate = this.formatPrice(value);
  }

  public async loadShop(): Promise<void> {
    this.isLoading = true;

    try {
      if (!this.shop) {
        this.shop = await this.shopService.getShopDetails(
          this.storageLocationId,
          DateUtil.serializeDate(new Date()),
          DateUtil.serializeDate(new Date()),
          1,
          0,
          'marketplace',
        );
      }
    } catch (err) {
      // TODO: Translate this
      this.notify.error(this.translate.instant('SHOP_COULD_NOT_BE_LOADED') as string);
    }

    this.isLoading = false;
  }

  public async save(): Promise<void> {
    if (this.isCustomPricingEnabled) {
      await this.createOrModifyContext();
    } else {
      await this.deleteContext();
    }
  }

  public cancel(): Promise<void> {
    return void this.router.navigate(['/dashboard'], { relativeTo: this.route, queryParams: null });
  }

  private async getCustomOrDefaultPricing(): Promise<void> {
    const response = await this.productService.getWalkInPricesForStorageLocation(
      this.storageLocationId,
      !this.isCustomPricingEnabled,
    );
    this.hourlyRate = this.formatPrice(response.products.find((p) => p.key === 'WALK_IN_HOURLY_RATE').cost);
    this.dailyRate = this.formatPrice(response.products.find((p) => p.key === 'WALK_IN_DAILY_RATE').cost);
  }

  private updateIsModified() {
    this.isModified =
      this.wasCustomPricingEnabled !== this.isCustomPricingEnabled ||
      this.hourlyRate !== this.previousHourlyRate ||
      this.dailyRate !== this.previousDailyRate;
    this.cd.markForCheck();
  }

  private async createOrModifyContext() {
    const products = [
      {
        productKey: 'WALK_IN_HOURLY_RATE',
        valueCost: { cost: this.hourlyRateNumber },
      },
      {
        productKey: 'WALK_IN_DAILY_RATE',
        valueCost: { cost: this.dailyRateNumber },
      },
    ];

    try {
      await this.productService.setWalkInPricesForStorageLocation(this.storageLocationId, products);
      void this.router.navigate(['/dashboard']);
    } catch (err) {
      // TODO: Translate this
      this.notify.error(this.translate.instant('PRICING_COULD_NOT_BE_SAVED') as string);
    }
  }

  private async deleteContext(): Promise<void> {
    try {
      await this.productService.deletePricingContext(this.storageLocationId);
      void this.router.navigate(['/dashboard'], { relativeTo: this.route, queryParams: null });
    } catch (err) {
      // TODO: Translate this
      this.notify.error(this.translate.instant('PRICING_COULD_NOT_BE_DELETED') as string);
    }
  }

  private formatPrice(value: string | number): string {
    if (typeof value !== 'number') {
      value = parseFloat(value);
    }

    if (isNaN(value)) {
      return '';
    }

    // Round price to 2 decimal places
    return value.toFixed(2);
  }
}
